import { Button, InputAdornment, LinearProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { Delivery, SdxAutocomplete, SdxTextField, Search } from '@sodexo/ui-components';
import debounce from 'lodash.debounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useAppInsightTrackEvent } from '../Effects/AppInsightEffect';
import { AppInsightEvent } from '../Models/AppInsight/AppInsightEvent';
import { Filters } from '../Models/Filters';
import type { Product } from '../Models/Product';
import { useAutocomplete } from '../Queries/search.queries';
import { useStore } from '../store/store';
import { Colors } from '../theme';

type SearchBarProps = {
  theme?: 'light' | 'dark';
};
export default function SearchBar({ theme = 'dark' }: SearchBarProps) {
  const { t } = useTranslation('', { keyPrefix: 'components.searchBar' });
  const navigate = useNavigate();
  const [catalog] = useStore(useShallow(state => [state.catalog!]));
  const trackEvent = useAppInsightTrackEvent();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const autocomplete = useAutocomplete(search, { placeholderData: previousData => previousData });

  const debounceFn = useCallback(
    debounce(search => {
      if (search) {
        setSearch(search);
        trackEvent(AppInsightEvent.SEARCH, { search });
      }
    }, 400),
    [trackEvent, setSearch],
  );

  const goToSku = (taxonId: string) => {
    trackEvent(AppInsightEvent.SEARCH_NAVIGATION, { goTo: 'sku' });
    const filters = new Filters();
    filters.productFilters.categories = [taxonId];
    navigate(`/products?${filters.buildUrlParams()}`, { state: { resetPage: true } });
    setOpen(false);
  };

  const goToSkuDetails = async (product: Product) => {
    const highestTaxonomy = product.getHighestTaxonomy(catalog.taxonomy);
    const filters = new Filters();
    filters.swapFilters.categories = [highestTaxonomy?._id.split('/')[1]!];
    filters.productFilters.categories = [highestTaxonomy?._id.split('/')[1]!];
    navigate(`/swaps/${product._key}?${filters.buildUrlParams()}`, { state: { resetPage: true } });
    setOpen(false);
  };

  const goToCategories = (taxonId: string) => {
    trackEvent(AppInsightEvent.SEARCH_NAVIGATION, { goTo: 'categories' });
    const filters = new Filters();
    filters.productFilters.categories = [taxonId];
    filters.swapFilters.categories = [taxonId];
    navigate(`/batch?${filters.buildUrlParams()}`, { state: { resetPage: true } });
    setOpen(false);
  };

  const Autocomplete = theme === 'dark' ? AutocompleteDark : AutocompleteLight;

  return (
    <Autocomplete
      freeSolo
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      ListboxProps={{
        style: { padding: 0 },
      }}
      filterOptions={x => x}
      options={[autocomplete]}
      getOptionLabel={option => ''}
      renderInput={params => {
        return (
          <SdxTextField
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position='start'>
                  <Search />
                </InputAdornment>
              ),
              onKeyDown: e => e.key === 'Enter' && e.stopPropagation(),
            }}
            onChange={e => debounceFn(e.target.value)}
            placeholder={t('placeholder', { catalog: catalog?.name.toUpperCase() }).toString()}
          />
        );
      }}
      renderOption={(props, options, state) => {
        return (
          <SearchOptions
            key='search-options'
            options={options as ReturnType<typeof useAutocomplete>}
            goToCategories={goToCategories}
            goToSku={goToSku}
            goToSkuDetails={goToSkuDetails}
          />
        );
      }}
    />
  );
}

const ResultHeader = styled(Stack)(({ theme }) => ({
  padding: 16,
  gap: 4,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  flex: 1,
  backgroundColor: Colors.lightestBlue,
}));

type SearchOptionsProps = {
  options?: ReturnType<typeof useAutocomplete>;
  goToSkuDetails: (product: Product) => void;
  goToCategories: (taxonId: string) => void;
  goToSku: (taxonId: string) => void;
};

function SearchOptions({ options, goToCategories, goToSku, goToSkuDetails }: SearchOptionsProps) {
  const { t } = useTranslation();
  if (!options) return null;

  return (
    <>
      {options.isFetching && <LinearProgress />}
      {!options.isFetching && options.data?.products.results.length === 0 && options.data.taxons.length === 0 && (
        <Typography m={2}>{t('components.searchBar.noResults')}</Typography>
      )}
      {options.data && (
        <>
          {options.data.taxons.length > 0 && (
            <ResultHeader>
              <Typography variant='body1Bold'>{t('common.taxon', { count: 2 })}</Typography>
            </ResultHeader>
          )}
          {options.data.taxons.map(taxon => (
            <Stack
              key={taxon._key}
              px={2}
              py={1}
              direction='row'
              spacing={2}
              justifyContent='space-between'
              alignItems='center'
              sx={{ ':hover': { backgroundColor: Colors.lighterBlue } }}
            >
              <Typography variant='body2' key={taxon._key}>
                {taxon.names.slice(1).reduce((text, value, i, array) => text + (i < array.length ? ' > ' : '') + value)}
              </Typography>
              <Stack height={30} spacing={2} direction='row'>
                <Button variant='contained' onClick={() => goToSku(taxon._key)}>
                  {t('components.searchBar.skuButton')}
                </Button>
                <Button variant='contained' onClick={() => goToCategories(taxon._key)}>
                  {t('components.searchBar.batch', { count: 0 })}
                </Button>
              </Stack>
            </Stack>
          ))}
          {options.data.products.results.length > 0 && (
            <ResultHeader>
              <Typography variant='body1Bold'>{t('common.product', { count: 2 })}</Typography>
              <Typography variant='caption'>
                {t('components.searchBar.results', { numberOfResults: options.data.products.numberOfResults })}
              </Typography>
            </ResultHeader>
          )}
          {options.data.products.results.map(product => (
            <Stack
              key={product._key}
              px={2}
              py={1}
              direction='row'
              spacing={2}
              justifyContent='space-between'
              alignItems='center'
              onClick={() => goToSkuDetails(product)}
              sx={{ cursor: 'pointer', ':hover': { backgroundColor: Colors.lighterBlue } }}
            >
              <Stack>
                <Stack direction='row' alignItems='center' spacing={0.5}>
                  <Typography variant='subtitle1'>{product.item_description_local_language}</Typography>
                  <Typography variant='subtitle1'>-</Typography>
                  <Typography variant='body2'>{product.supplier_item_code}</Typography>
                </Stack>
                <Stack direction='row' alignItems='flex-start'>
                  <Delivery height={16} />
                  <Typography variant='subtitle2'>{product.getAttribute('Attributes/supplier_name')?.value}</Typography>
                </Stack>
              </Stack>
              <Button sx={{ height: 30 }} variant='contained' onClick={() => goToSkuDetails(product)}>
                {t('common.detail', { count: 0 })}
              </Button>
            </Stack>
          ))}
        </>
      )}
    </>
  );
}

const AutocompleteDark = styled(SdxAutocomplete)(({ theme }) => ({
  backgroundColor: '#1D1C43',
  maxWidth: 700,
  borderBottom: '1px solid white',
  flexGrow: 1,
  '.MuiOutlinedInput-root': {
    borderRadius: 0,
    backgroundColor: 'transparent',
    input: {
      color: 'white',
    },
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 0,
    borderRadius: 0,
  },
}));

const AutocompleteLight = styled(SdxAutocomplete)(({ theme }) => ({
  width: '100%',
}));
